<template>
  <ForwardsPageComponent>
    <LoadingIndicatorComponent
      v-if="store.loading && store.deal === null"
      :message="$t('forwards.deals.loading')"
    />

    <div v-else-if="store.notFound" class="card">
      <div class="card-body text-center py-5">
        <FontAwesomeIcon
          icon="fa-solid fa-lock"
          size="2x"
          class="text-body-tertiary mb-3"
        />
        <h5>{{ $t("forwards.deals.not_found_title") }}</h5>
        <p class="text-body-tertiary mb-3">
          {{ $t("forwards.deals.not_found_body") }}
        </p>
        <RouterLink
          :to="{ name: 'ForwardsOverview' }"
          class="btn btn-outline-primary btn-sm"
          >{{ $t("forwards.deals.header.back") }}</RouterLink
        >
      </div>
    </div>

    <div v-else-if="store.deal === null" class="card">
      <div class="card-body text-center py-5">
        <h5>{{ $t("forwards.deals.load_failed_title") }}</h5>
        <p class="text-body-tertiary mb-3">
          {{ $t("forwards.deals.load_failed_body") }}
        </p>
        <button
          type="button"
          class="btn btn-outline-primary btn-sm"
          @click="reload"
        >
          {{ $t("forwards.common.retry") }}
        </button>
      </div>
    </div>

    <template v-else>
      <DealHeaderComponent :deal="store.deal" />

      <div
        v-if="store.deal.status === 'draft'"
        class="alert alert-warning d-flex align-items-center gap-2 mb-3"
        role="alert"
        data-testid="draft-deal-warning"
      >
        <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
        <span>{{ $t("forwards.deals.draft_warning") }}</span>
      </div>

      <PriceConflictAlertsComponent :deliveries="store.deal.deliveries" />

      <p v-if="headerErrorMessage" class="error-note mb-3" role="alert">
        <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
        <span>{{ headerErrorMessage }}</span>
      </p>

      <p
        v-for="(warning, index) in commitmentWarnings"
        :key="index"
        class="advisory-note mb-3"
      >
        <FontAwesomeIcon icon="fa-regular fa-circle-info" />
        <span>{{ warning }}</span>
      </p>

      <div
        v-if="combinedDealProfile"
        class="card border mb-3"
        data-testid="combined-profile"
      >
        <div class="card-header">
          <h5 class="mb-0">
            {{ $t("forwards.deals.plan.profile.combined_title") }}
          </h5>
        </div>
        <div class="card-body">
          <p class="text-body-tertiary fs-10 mb-2">
            {{ $t("forwards.deals.plan.profile.judged_per_delivery") }}
          </p>
          <ProfileBreakdownsComponent :profile="combinedDealProfile" />
        </div>
      </div>

      <div class="row g-3">
        <div class="col-lg-8 d-flex flex-column">
          <template v-if="store.isPayAsProduced">
            <PapScheduleCardComponent
              ref="papScheduleCard"
              v-model:editing="editingPapSchedule"
              :windowStart="store.deal.pap_window_start"
              :windowEnd="store.deal.pap_window_end"
              :tolerance="store.deal.pap_volume_tolerance"
              :months="papMonths"
              :deliveries="store.deal.deliveries"
              :planVersion="store.deal.plan_version"
              :mandates="store.commitmentGroups"
              :canEdit="store.deal.capabilities.can_edit_pap_schedule"
              :canCorrectShipped="store.deal.capabilities.can_correct_shipped"
              :canCancel="store.deal.capabilities.can_cancel"
              :busy="store.busyAction === 'pap_schedule'"
              :busyAction="store.busyAction"
              :errorMessage="store.errorOn('pap-schedule')"
              @save="onSavePapSchedule"
              @cancel="openCancelDialog"
            />
            <PapMonthlyMonitoringComponent
              :months="papMonths"
              :deal="store.deal"
              :managesDeal
              :loading="store.papMonitoringLoading"
              @delivered="reload"
              @producer-prices="openProducerPricesModal"
            />
          </template>

          <template v-else>
            <SaleNoticesCardComponent
              v-if="store.deal.sale_notices.length > 0"
              :notices="store.deal.sale_notices"
              :canSkip="managesDeal"
              :busy="store.busyAction === 'skip_notices'"
              :notSkippedCount
              @skip="openSkipNoticesDialog"
            />
            <DeliveryPlanEditorComponent
              v-if="showEditor"
              :deal="store.deal"
              :preview="store.planPreview"
              :busy="store.busyAction === 'plan'"
              :errorMessage="store.errorOn('plan-editor')"
              @dry-run="store.previewPlan"
              @discard="closeEditor"
              @save="onSavePlan"
            />
            <DeliveryPlanSectionComponent
              v-else
              :deal="store.deal"
              :offeringSheetUrls="store.offeringSheetUrls"
              :busyAction="store.busyAction"
              :highlightedDeliveryId="highlightedDeliveryId"
              :scrollToHighlighted="
                highlightedDeliveryId !== scrolledDeliveryId
              "
              @edit-plan="showEditor = true"
              @generate-offering-sheet="store.generateOfferingSheet"
              @slice-action="onSliceAction"
              @delivered="reload"
              @producer-prices="openProducerPricesModal"
              @delivery-notes="openDeliveryNotesModal"
              @confirm="store.confirm"
              @unconfirm="openUnconfirmDialog"
              @cancel="openCancelDialog"
              @scrolled="scrolledDeliveryId = $event"
            />
          </template>

          <FreeEscrowCardComponent
            v-if="store.freeEscrow.length > 0"
            :rows="store.freeEscrow"
            :canReturn="managesDeal"
            @return-to-owner="openEscrowModal(null)"
          />
        </div>

        <div class="col-lg-4">
          <DealMetadataCardComponent
            v-model:editingCriteria="editingCriteria"
            :deal="store.deal"
            :busyAction="store.busyAction"
            :criteriaPreview
            @edit-metadata="openDialog({ kind: 'metadata' })"
            @save-criteria="onSaveCriteria"
          />
        </div>
      </div>

      <CommitmentsSectionComponent
        v-if="!store.isPayAsProduced"
        :groups="store.commitmentGroups"
        @show-escrow="openEscrowModal"
      />

      <DealMetadataEditModal
        v-model="metadataOpen"
        :deal="store.deal"
        :busy="store.busyAction === 'metadata'"
        :errorMessage="store.errorOn('metadata')"
        @submit="onSaveMetadata"
      />

      <DealSliceDialogsComponent
        ref="sliceDialogs"
        @warnings="commitmentWarnings = $event"
      />

      <DealEscrowDialogsComponent ref="escrowDialogs" />

      <ProducerPricesModal
        ref="producerPricesModal"
        v-model="producerPricesOpen"
        :delivery="pricedDelivery"
        :planVersion="store.deal.plan_version"
        :readOnly="!managesDeal || pricedDeliveryFrozen"
        :busy="store.busyAction === 'producer_prices'"
        :errorMessage="store.errorOn('producer-prices')"
        @submit="onSaveProducerPrices"
      />

      <DeliveryNotesModal
        v-model="deliveryNotesOpen"
        :delivery="notedDelivery"
        :busy="store.busyAction === 'delivery_notes'"
        :errorMessage="store.errorOn('delivery-notes')"
        @submit="onSaveDeliveryNotes"
      />

      <ConfirmationModalComponent
        :visible="isOpen('unconfirm')"
        :title="$t('forwards.deals.plan.unconfirm_title')"
        :message="$t('forwards.deals.plan.unconfirm_body')"
        :confirmButtonText="$t('forwards.deals.plan.unconfirm')"
        confirmButtonClass="btn-primary"
        :closeOnConfirm="false"
        :busy="store.busyAction === 'unconfirm'"
        :errorMessage="store.errorOn('unconfirm')"
        @confirm="onUnconfirmDeal"
        @cancel="closeDialog('unconfirm')"
      />

      <ConfirmationModalComponent
        :visible="isOpen('skip-notices')"
        :title="$t('forwards.deals.sale_notices.skip_title')"
        :message="skipNoticesMessage"
        :confirmButtonText="$t('forwards.deals.sale_notices.skip_confirm')"
        confirmButtonClass="btn-danger"
        :closeOnConfirm="false"
        :busy="store.busyAction === 'skip_notices'"
        :errorMessage="store.errorOn('skip-notices')"
        @confirm="onSkipNotices"
        @cancel="closeDialog('skip-notices')"
      />

      <ConfirmationModalComponent
        :visible="isOpen('cancel')"
        :title="$t('forwards.deals.plan.cancel_title')"
        :message="
          $t(
            store.isPayAsProduced
              ? 'forwards.deals.plan.cancel_body_pap'
              : 'forwards.deals.plan.cancel_body'
          )
        "
        :confirmButtonText="$t('forwards.deals.plan.cancel_deal')"
        confirmButtonClass="btn-danger"
        :closeOnConfirm="false"
        :busy="store.busyAction === 'cancel'"
        :errorMessage="store.errorOn('cancel')"
        @confirm="onCancelDeal"
        @cancel="closeDialog('cancel')"
      />
    </template>

    <!-- A mandate edited from one of this deal's mandate links can change its feasibility and commitments. -->
    <MandateEditorModalHost v-if="mandateEditorAvailable" @written="reload" />
  </ForwardsPageComponent>
</template>

<script setup lang="ts">
import ConfirmationModalComponent from "@/components/common/ConfirmationModalComponent.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { useQueryManager } from "@/composables/useQueryManager";
import { useAccountStore } from "@/composables/account/useAccountStore";
import { managesClient } from "@/composables/useAdminCheck";
import ForwardsPageComponent from "@/forwards2/components/ForwardsPageComponent.vue";
import { provideMandateEditorAvailable } from "@/forwards2/components/mandateEditorAvailability";
import MandateEditorModalHost from "@/forwards2/mandates/MandateEditorModalHost.vue";
import CommitmentsSectionComponent from "@/forwards2/deals/CommitmentsSectionComponent.vue";
import DealEscrowDialogsComponent from "@/forwards2/deals/DealEscrowDialogsComponent.vue";
import DealHeaderComponent from "@/forwards2/deals/DealHeaderComponent.vue";
import DealMetadataCardComponent from "@/forwards2/deals/DealMetadataCardComponent.vue";
import DealMetadataEditModal from "@/forwards2/deals/DealMetadataEditModal.vue";
import DealSliceDialogsComponent from "@/forwards2/deals/DealSliceDialogsComponent.vue";
import DeliveryPlanEditorComponent from "@/forwards2/deals/DeliveryPlanEditorComponent.vue";
import DeliveryPlanSectionComponent from "@/forwards2/deals/DeliveryPlanSectionComponent.vue";
import FreeEscrowCardComponent from "@/forwards2/deals/FreeEscrowCardComponent.vue";
import PapMonthlyMonitoringComponent from "@/forwards2/deals/PapMonthlyMonitoringComponent.vue";
import PapScheduleCardComponent from "@/forwards2/deals/PapScheduleCardComponent.vue";
import PriceConflictAlertsComponent from "@/forwards2/deals/PriceConflictAlertsComponent.vue";
import ProducerPricesModal from "@/forwards2/deals/ProducerPricesModal.vue";
import ProfileBreakdownsComponent from "@/forwards2/deals/ProfileBreakdownsComponent.vue";
import SaleNoticesCardComponent from "@/forwards2/deals/SaleNoticesCardComponent.vue";
import DeliveryNotesModal from "@/forwards2/deals/DeliveryNotesModal.vue";
import { isDeliveryFrozen } from "@/forwards2/deals/deliveryFrozen";
import { combinedProfile } from "@/forwards2/deals/deliveryProfile";
import type { SliceAction } from "@/forwards2/deals/sliceActions";
import { useActiveDialog } from "@/forwards2/deals/useActiveDialog";
import { useDealStore } from "@/forwards2/deals/useDealStore";
import { disposePageStore } from "@/forwards2/disposePageStore";
import type {
  CriteriaPreview,
  DealMetadataRequest,
  DeliveryResponse,
  EligibilityCriteriaRequest,
  PapScheduleUpdateRequest,
  PlanStateRequest,
  ProducerPricesRequest,
  DeliveryNotesRequest,
} from "@/forwards2/forwardsTypes";
import {
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  ref,
  shallowRef,
  useTemplateRef,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink, useRoute } from "vue-router";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

/**
 * The page's own modal dialogs; the slice and escrow dialogs live in their own components. Each entry
 * carries only the ids its dialog reads the store by.
 */
type DealDialog =
  | { kind: "producer-prices"; deliveryId: number }
  | { kind: "delivery-notes"; deliveryId: number }
  | { kind: "metadata" }
  | { kind: "unconfirm" }
  | { kind: "skip-notices"; mandateIds: number[] }
  | { kind: "cancel" };

/**
 * One forward deal: its buyer and product definition, its delivery plan, and the commitments and
 * escrow behind it. A pay-as-produced deal has a generated plan instead, so it shows its nominated
 * schedule and monthly monitoring — the same page for every viewer, only the controls differ
 * (user decision, 2026-09-24).
 *
 * Every mutating call answers with the whole deal, so nothing here re-fetches after an action —
 * except the escrow moves, which answer with per-item results instead and are followed by a
 * reload. A deal the caller may not see is a 404, exactly like one that does not exist.
 */
const route = useRoute();
const store = useDealStore();
const accountStore = useAccountStore();
const { getQueryValue, parsers } = useQueryManager();
const { globalErrorHandler } = useGlobalHandlers();
const { t } = useI18n();

const deliveryParam = getQueryValue("delivery", parsers.string);

const showEditor = ref(false);
const editingPapSchedule = ref(false);
const papScheduleCard = useTemplateRef("papScheduleCard");
const producerPricesModal = useTemplateRef("producerPricesModal");
const editingCriteria = ref(false);
const criteriaPreview = shallowRef<CriteriaPreview | null>(null);
const commitmentWarnings = ref<string[]>([]);
// How many mandates the last skip found with nothing unsent: the server left them as they are and says so.
const notSkippedCount = ref(0);
const sliceDialogs = useTemplateRef("sliceDialogs");
const escrowDialogs = useTemplateRef("escrowDialogs");
// Every dialog clears the action error on opening, except the metadata dialog, which has always left a
// stale error where it was.
const {
  isOpen,
  open: openDialog,
  close: closeDialog,
  entry: dialogEntry,
  model: dialogModel,
} = useActiveDialog<DealDialog>();
// The delivery `?delivery=` was last scrolled to, kept here because the cards remount whenever the
// plan editor closes.
const scrolledDeliveryId = ref<number | null>(null);

const routeDealId = computed(() => {
  const raw = route.params.id;
  const value = Array.isArray(raw) ? raw[0] : raw;
  const parsed = Number(value);
  return value === undefined || Number.isNaN(parsed) ? null : parsed;
});

const highlightedDeliveryId = computed(() => {
  const parsed = Number(deliveryParam.value);
  return deliveryParam.value && !Number.isNaN(parsed) ? parsed : null;
});

const managesDeal = computed(
  () => store.deal?.capabilities.can_manage === true
);
// The deal reads platform-wide as the admin, but the mandate editor reads as the session's account: a
// managed client sees only its own mandates, so a link would open on a 404. The admin guard loaded the
// account on entering the page.
const mandateEditorAvailable = computed(
  () => !managesClient(accountStore.account)
);
const papMonths = computed(() => store.papMonitoring?.months ?? []);

/** Not shown for a deal with no planned volume to profile, a pay-as-produced deal among them. */
const combinedDealProfile = computed(() =>
  store.deal ? combinedProfile(store.deal.deliveries) : null
);

const headerErrorMessage = computed(() => store.errorOn("header"));

const metadataOpen = dialogModel("metadata");
const producerPricesOpen = dialogModel("producer-prices");
const deliveryNotesOpen = dialogModel("delivery-notes");

const pricedDialog = dialogEntry("producer-prices");
const skipNoticesDialog = dialogEntry("skip-notices");
const notedDialog = dialogEntry("delivery-notes");

const pricedDeliveryId = computed(() => pricedDialog.value?.deliveryId ?? null);

const pricedDelivery = computed<DeliveryResponse | null>(
  () =>
    store.deliveries.find(
      (delivery) => delivery.id === pricedDeliveryId.value
    ) ?? null
);

/** A shipped delivery's prices are what it invoiced at, and a voided one has no transaction left to price,
 *  so both are shown, never changed. */
const pricedDeliveryFrozen = computed(
  () =>
    pricedDelivery.value !== null &&
    isDeliveryFrozen(pricedDelivery.value.transaction_status)
);

const notedDelivery = computed<DeliveryResponse | null>(
  () =>
    store.deliveries.find(
      (delivery) => delivery.id === notedDialog.value?.deliveryId
    ) ?? null
);

const reload = async () => {
  if (routeDealId.value !== null) await store.load(routeDealId.value);
  if (store.isPayAsProduced) await store.loadPapMonitoring();
};

watch(routeDealId, () => {
  // The schedule draft is keyed by month label, so another deal's months must not inherit it.
  // Closing the criteria editor also ends a pending criteria preview's session (see onSaveCriteria).
  editingPapSchedule.value = false;
  editingCriteria.value = false;
  void reload();
});

// A preview belongs to one editing session: reopened, the editor starts from the deal's criteria again.
watch(editingCriteria, (editing) => {
  if (!editing) criteriaPreview.value = null;
});

watch(
  () => store.loadError,
  (error) => {
    if (error !== null) globalErrorHandler(error);
  }
);

watch(
  () => store.actionError,
  (error) => {
    if (error !== null) globalErrorHandler(error);
  }
);

onMounted(() => void reload());

// The store is page-scoped: disposing it with its state is what lets the next deal open on the
// loading state rather than on this one.
onBeforeUnmount(() => disposePageStore(store));

const closeEditor = () => {
  showEditor.value = false;
  store.clearPlanPreview();
};

const onSavePlan = async (state: PlanStateRequest) => {
  if ((await store.savePlan(state)) !== null) closeEditor();
};

const onSaveMetadata = async (body: DealMetadataRequest) => {
  if ((await store.saveMetadata(body)) !== null) closeDialog("metadata");
};

/**
 * The editor stays open until the save has actually landed, so a refusal keeps the entered numbers.
 * A stale save has had the deal reloaded by the store; once the card renders it, the card merges
 * its draft onto it.
 */
const onSavePapSchedule = async (body: PapScheduleUpdateRequest) => {
  const outcome = await store.savePapSchedule(body);
  if (outcome === "saved") editingPapSchedule.value = false;
  if (outcome !== "conflict") return;
  await nextTick();
  papScheduleCard.value?.mergeSavedSchedule();
};

/**
 * The same rule for the criteria: a refused save keeps the form and its message (RV2-plan-editing-05).
 * Each press first previews the entered criteria and saves only when nothing conflicts; warnings wait
 * for a second press that acknowledges them. A refused save drops the preview, so the next press
 * asks again rather than saving over a conflict that appeared meanwhile. A preview answering after
 * the editor closed or the page moved to another deal is dropped: its save would write criteria
 * nobody is editing any more, or onto the other deal.
 */
const onSaveCriteria = async (
  body: EligibilityCriteriaRequest,
  acknowledged: boolean
) => {
  if (!acknowledged) {
    const dealId = store.dealId;
    const preview = await store.previewCriteria(body);
    if (preview === null || !editingCriteria.value || store.dealId !== dealId)
      return;
    criteriaPreview.value = {
      body,
      blocking: preview.blocking,
      warnings: preview.warnings,
    };
    if (preview.blocking.length > 0 || preview.warnings.length > 0) return;
  }
  if ((await store.saveCriteria(body)) === null) {
    criteriaPreview.value = null;
    return;
  }
  editingCriteria.value = false;
};

const onSliceAction = (action: SliceAction) => {
  if (action.kind === "escrow-pull")
    escrowDialogs.value?.openPull(action.commitmentId);
  else {
    commitmentWarnings.value = [];
    sliceDialogs.value?.open(action.kind, action.sliceId);
  }
};

const openEscrowModal = (commitmentId: number | null) =>
  escrowDialogs.value?.openReturn(commitmentId);

const openProducerPricesModal = (deliveryId: number) => {
  store.clearActionError();
  openDialog({ kind: "producer-prices", deliveryId });
};

/**
 * The map is the delivery's whole set of mandate prices: a mandate left out pays the default. A
 * stale save keeps the dialog open and merges its draft onto the reloaded delivery, as the
 * schedule card does.
 */
const onSaveProducerPrices = async (request: ProducerPricesRequest) => {
  if (pricedDeliveryId.value === null) return;
  const outcome = await store.saveProducerPrices(
    pricedDeliveryId.value,
    request
  );
  if (outcome === "saved") closeDialog("producer-prices");
  if (outcome !== "conflict") return;
  await nextTick();
  producerPricesModal.value?.mergeSavedPrices();
};

const openDeliveryNotesModal = (deliveryId: number) => {
  store.clearActionError();
  openDialog({ kind: "delivery-notes", deliveryId });
};

const onSaveDeliveryNotes = async (request: DeliveryNotesRequest) => {
  const delivery = notedDelivery.value;
  if (delivery === null) return;
  const result = await store.saveDeliveryNotes(delivery.id, request);
  if (result !== null) closeDialog("delivery-notes");
};

const openUnconfirmDialog = () => {
  store.clearActionError();
  openDialog({ kind: "unconfirm" });
};

const onUnconfirmDeal = async () => {
  const result = await store.unconfirm();
  if (result === null) return;
  closeDialog("unconfirm");
};

const skipNoticesMessage = computed(() =>
  t("forwards.deals.sale_notices.skip_body", {
    mandates: t(
      "forwards.deals.sale_notices.mandate_count",
      skipNoticesDialog.value?.mandateIds.length ?? 0
    ),
  })
);

const openSkipNoticesDialog = (mandateIds: number[]) => {
  store.clearActionError();
  notSkippedCount.value = 0;
  openDialog({ kind: "skip-notices", mandateIds });
};

const onSkipNotices = async () => {
  const mandateIds = skipNoticesDialog.value?.mandateIds ?? [];
  const result = await store.skipSaleNotices(mandateIds);
  if (result === null) return;
  notSkippedCount.value = result.not_skipped_mandate_ids.length;
  closeDialog("skip-notices");
};

const openCancelDialog = () => {
  store.clearActionError();
  openDialog({ kind: "cancel" });
};

const onCancelDeal = async () => {
  const result = await store.cancel();
  if (result === null) return;
  closeDialog("cancel");
};

provideMandateEditorAvailable(mandateEditorAvailable);
</script>
